import { TrendingUp, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext";
import { useDistrict } from "../utils/DistrictContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Sankey,
  Layer,
  Rectangle,
} from "recharts";
import { getTotalConsumption } from "../services/index";

const SankeyLink = (props) => {
  const { sourceX, targetX, sourceY, targetY, linkWidth, payload } = props;
  if (linkWidth <= 0) return null;

  // 1. Define the same offset used in your SankeyNode
  const POTABLE_SUPPLY_OFFSET = -18;

  // 2. Adjust coordinates based on the payload names
  // We adjust the Y position if the source or target is the node we moved
  let adjustedSourceY = sourceY;
  let adjustedTargetY = targetY;

  if (payload.source.name === "Potable supply") {
    adjustedSourceY += POTABLE_SUPPLY_OFFSET;
  }
  if (payload.target.name === "NP supply") {
    adjustedTargetY += 5;
  }

  const curvature = 0.5;
  const x0 = sourceX;
  const x1 = targetX;
  const xi = x0 * (1 - curvature) + x1 * curvature;
  const xj = x0 * curvature + x1 * (1 - curvature);

  // Use the adjusted Y values for the path
  const d = `M${x0},${adjustedSourceY} C${xi},${adjustedSourceY} ${xj},${adjustedTargetY} ${x1},${adjustedTargetY}`;

  return (
    <Layer>
      <path
        d={d}
        fill="none"
        stroke={payload.color || "#444"}
        strokeWidth={Math.max(linkWidth * 0.2, 2)}
        strokeOpacity={0.4}
        style={{ pointerEvents: "none" }}
      />

      <path
        d="M-4,-4 L4,0 L-4,4 Z"
        fill={payload.color}
        fillOpacity={0.3}
        transform={`translate(${(x0 + x1) / 2}, ${(adjustedSourceY + adjustedTargetY) / 2}) rotate(${
          ((adjustedTargetY - adjustedSourceY) / (targetX - x0)) * 45
        })`}
      />
    </Layer>
  );
};
const SankeyNode = ({ x, y, width, height, payload }) => {
  const nodeWidth = 120;
  const nodeHeight = 65;
  const yOffset =
    payload.name === "Potable supply"
      ? -2
      : payload.name === "Total demand"
        ? 10
        : 0;
  const rx = x - nodeWidth / 2 + width / 2;
  // const ry = y + height / 2 - nodeHeight / 2;
  const ry = y + height / 2 - nodeHeight / 2 + yOffset;

  return (
    <Layer>
      <Rectangle
        x={rx}
        y={ry}
        width={nodeWidth}
        height={nodeHeight}
        fill={payload.color}
        radius={8}
      />
      <text
        x={rx + 15}
        y={ry + 20}
        fontSize="10"
        fontWeight="700"
        fill="rgba(0,0,0,0.5)"
        style={{ textTransform: "uppercase" }}
      >
        {payload.name}
      </text>
      <text x={rx + 15} y={ry + 50} fontSize="14" fontWeight="800" fill="#000">
        {payload.displayValue != null
          ? `${payload.displayValue.toLocaleString()} K`
          : "--"}
      </text>
    </Layer>
  );
};

export function TotalConsumption() {
  const { theme, colors } = useTheme();
  const { selectedDistrict } = useDistrict();
  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const buttonRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [selectedDays, setSelectedDays] = useState("Past Month");
  const timeOptions = [
    { label: "D", value: "Past Day" },
    { label: "W", value: "Past Week" },
    { label: "1M", value: "Past Month" },
    { label: "3M", value: "Past 3 Months" },
    { label: "Y", value: "Past Year" },
  ];
  const isFB151 = selectedDistrict === "FB151";

  const FLOW_COLORS = useMemo(
    () => ({
      potable: colors.primary || "#4A90E2",
      nonPotable: "#A855F7",
      total: colors.success || "#71c2a8",
      chartPalette: [
        "#4A90E2",
        "#50E3C2",
        "#B8E986",
        "#FF6F61",
        "#f89256",
        "#fbc45a",
      ],
    }),
    [colors],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // NEW: Pass selectedDays to the API function
        const response = await getTotalConsumption(selectedDays);
        if (response.data.status) setApiData(response.data.consumption);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedDistrict, selectedDays]); // <-- ADD selectedDays to dependency array

  useEffect(() => {
    if (showInfoPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      setPopupPosition({
        top: rect.top + window.scrollY, // important
        left: rect.right + 10 + window.scrollX,
      });
    }
  }, [showInfoPopup]);

  const getSafeValue = (val) => (val ? Math.round(val / 1000) : 0);

  // Data for BarChart (FB151)
  const barChartData = useMemo(() => {
    if (!apiData || !isFB151) return [];

    // Define only the two required metrics
    return [
      {
        id: "potable-supply",
        label: "Potable Plant Supply",
        name: "Potable Plant\nSupply",
        value: getSafeValue(apiData.supply_total_consumption),
      },
      {
        id: "potable-demand",
        label: "Potable Demand",
        name: "Potable\nDemand",
        value: getSafeValue(
          apiData.potable_demand_total_consumption ||
            apiData.demand_total_consumption_total,
        ),
      },
    ];
  }, [apiData, isFB151]);

  const sankeyData = useMemo(() => {
    if (!apiData || isFB151) return { nodes: [], links: [] };
    const nodes = [
      {
        name: "Potable intake",
        displayValue: getSafeValue(apiData.produced_total_consumption),
        color: FLOW_COLORS.potable,
      },
      {
        name: "Potable supply",
        displayValue: getSafeValue(apiData.supply_total_consumption),
        color: FLOW_COLORS.potable,
      },
      {
        name: "Potable demand",
        displayValue: getSafeValue(apiData.potable_demand_total_consumption),
        color: FLOW_COLORS.potable,
      },
      { name: "NP plant", displayValue: null, color: FLOW_COLORS.nonPotable },
      {
        name: "NP supply",
        displayValue: getSafeValue(
          apiData.non_potable_supply_total_consumption,
        ),
        color: FLOW_COLORS.nonPotable,
      },
      {
        name: "NP demand",
        displayValue: getSafeValue(
          apiData.non_potable_demand_total_consumption,
        ),
        color: FLOW_COLORS.nonPotable,
      },
      {
        name: "Total demand",
        displayValue: getSafeValue(apiData.total_demand),
        color: FLOW_COLORS.total,
      },
    ];
    const links = [
      { source: 0, target: 1, value: 10, color: FLOW_COLORS.potable },
      { source: 1, target: 2, value: 8, color: FLOW_COLORS.potable },
      { source: 2, target: 6, value: 8, color: FLOW_COLORS.potable },
      { source: 3, target: 4, value: 6, color: FLOW_COLORS.nonPotable },
      { source: 4, target: 5, value: 6, color: FLOW_COLORS.nonPotable },
      { source: 5, target: 6, value: 6, color: FLOW_COLORS.nonPotable },
      { source: 1, target: 4, value: 3, color: "#6a88b5" },
    ];
    return { nodes, links };
  }, [apiData, isFB151, FLOW_COLORS]);

  const renderDataPoint = (label, value, color) => (
    <div
      className="flex flex-col border-l-[3px] pl-4 py-1"
      style={{ borderColor: color }}
    >
      <span className="text-[11px] text-slate-400 font-semibold mb-1 uppercase tracking-wider">
        {label}
      </span>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold text-white">
          {value != null ? value.toLocaleString() : "--"}
        </span>
        {value != null && (
          <span className="text-[12px] text-slate-400 font-medium">K</span>
        )}
      </div>
    </div>
  );

  return (
    <motion.div
      className="rounded-xl p-5 transition-colors duration-300 relative overflow-hidden group flex flex-col h-full"
      style={{
        backgroundColor:
          theme === "white" ? "#ffffff" : colors.glassEffect || "#0f172a",
        borderColor: colors.border,
        backdropFilter: theme === "white" ? "none" : "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ x: 10 }}
    >
      <div
        className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-400"
        style={{ backgroundColor: colors.primary }}
      />

      <div className="relative z-10 flex flex-col h-full max-[992px]:overflow-x-auto max-[1750px]:overflow-x-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <h3
              className="font-semibold tracking-tight text-[24px]"
              style={{ color: colors.text }}
            >
              Total Consumption
            </h3>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowInfoPopup((prev) => !prev)}
                className="p-1 rounded-full hover:bg-white/10"
                style={{ color: colors.primary }}
                aria-label="Show information"
              >
                <Info size={16} />
              </button>

              <AnimatePresence>
                {showInfoPopup && (
                  <motion.div
                    className="absolute left-full top-1/2 ml-2 -translate-y-1/2 rounded-lg shadow-xl border z-[9999] whitespace-nowrap"
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      borderColor: `${colors.primary}30`,
                      boxShadow: `0 8px 32px ${colors.primary}30`,
                    }}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                  >
                    <div className="px-4 py-3">
                      <p
                        className="text-sm leading-relaxed"
                        style={{ color: colors.textSecondary }}
                      >
                        This metric is calculated from
                        <br />
                        networked meters only
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <div
            className="flex items-center p-1 rounded-lg border bg-opacity-20 shrink-0 cursor-pointer ml-auto"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
            }}
          >
            {timeOptions.map((tp) => {
              const isActive = selectedDays === tp.value;
              return (
                <button
                  key={tp.value}
                  onClick={() => setSelectedDays(tp.value)}
                  className="px-2.5 py-1 rounded-md text-[10px] min-[1921px]:text-[12px] font-black transition-all duration-200 uppercase cursor-pointer"
                  style={{
                    backgroundColor: isActive ? colors.primary : "transparent",
                    color: isActive ? "#ffffff" : colors.textSecondary,
                    boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
                  }}
                >
                  {tp.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart Render */}

        <div className="flex-1 min-h-[400px] max-[992px]:w-[700px] max-[1750px]:w-[700px]">
          {!isLoading &&
            apiData &&
            (isFB151 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barChartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 40 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={
                      theme === "white" ? "#e0e0e0" : `${colors.border}40`
                    }
                  />
                  <XAxis
                    dataKey="name"
                    stroke={colors.textSecondary}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    stroke={colors.textSecondary}
                    tickFormatter={(val) => `${val}K`}
                    width={80}
                  />
                  <Tooltip cursor={{ fill: `${colors.primary}10` }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={60}>
                    {barChartData.map((entry, index) => (
                      <Cell
                        key={entry.id}
                        fill={FLOW_COLORS.chartPalette[index % 6]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <Sankey
                  data={sankeyData}
                  node={<SankeyNode />}
                  link={<SankeyLink />}
                  nodeWidth={1}
                  nodePadding={260}
                  margin={{ top: 20, left: 60, right: 60, bottom: 20 }}
                >
                  <Tooltip formatter={(val) => [`${val} K Gallons`, "Flow"]} />
                </Sankey>
              </ResponsiveContainer>
            ))}
        </div>

        {/* --- CONDITIONAL FOOTER RENDER --- */}
        <div
          className="mt-8 pt-8 border-t"
          style={{ borderColor: `${colors.border}40` }}
        >
          {isFB151 ? (
            /* PREVIOUS TYPE FOOTER (Simple Grid) */
            <div className="w-full grid grid-cols-3 gap-3 max-[451px]:grid-cols-2">
              {barChartData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2.5 rounded-lg border transition-all"
                  style={{
                    backgroundColor: `${FLOW_COLORS.chartPalette[index % 6]}0d`,
                    borderColor: colors.border,
                  }}
                >
                  <div
                    className="w-3 h-3 rounded flex-shrink-0"
                    style={{
                      backgroundColor: FLOW_COLORS.chartPalette[index % 6],
                    }}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span
                      className="text-[10px] font-medium truncate"
                      style={{ color: colors.textSecondary }}
                    >
                      {item.label}
                    </span>
                    <span
                      className="text-xs font-bold"
                      style={{ color: colors.text }}
                    >
                      {item.value.toLocaleString()} K
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
